import { BaseAPI, Configuration } from "../api_client";
import { Constants } from "../helpers";
import { AuthService } from "../services/AuthService";
import {
  CareOrganisationsApi,
  CareUnitsApi,
  GreetingsApi,
  RotasApi,
  ShiftPresetsApi,
  ShiftsApi,
  SurveyTemplatesApi,
  SurveysApi,
  UsersApi,
} from "./apis";
import { NotLoggedInError } from "./NotLoggedInError";
import { RequestOpts } from "./runtime";

// Having to subclass every API manually is annoying, but there doesn't seem to be a
// better way to do it under the constraints of the auto-generated client code.

type Constructor = new (...args: any[]) => BaseAPI;

function withOAuth<TAPICtor extends Constructor>(Base: TAPICtor) {
  return class OAuthAPI extends Base {
    private constructor(...args: any[]) {
      super(...args);
    }

    private setAccessToken(
      accessToken: string | undefined,
      context: RequestOpts,
    ) {
      const currentConfigParams = (this.configuration as any).configuration;

      const authHeader = `Bearer ${accessToken}`;
      this.configuration = new Configuration({
        ...currentConfigParams,
        accessToken: authHeader,
      });

      context.headers.Authorization = authHeader;
    }

    /* eslint-disable camelcase */
    protected async request(context: RequestOpts): Promise<Response> {
      const auth = AuthService.getInstance();
      let user = await auth.getUser();

      if (user === null) {
        throw new NotLoggedInError();
      }

      if (user.access_token === undefined) {
        user = await auth.renewToken();
      }

      this.setAccessToken(user?.access_token, context);

      try {
        return await super.request(context);
      } catch (response) {
        if (response.status === 401) {
          user = await auth.renewToken();
          this.setAccessToken(user?.access_token, context);
          return await super.request(context);
        }

        throw response;
      }
    }
    /* eslint-enable camelcase */
  };
}

const CQECareOrganisationsAPI = withOAuth(CareOrganisationsApi);
const CQECareUnitsAPI = withOAuth(CareUnitsApi);
const CQEGreetingsAPI = withOAuth(GreetingsApi);
const CQERotasAPI = withOAuth(RotasApi);
const CQEShiftsAPI = withOAuth(ShiftsApi);
const CQESurveysAPI = withOAuth(SurveysApi);
const CQESurveyTemplatesAPI = withOAuth(SurveyTemplatesApi);
const CQEUsersAPI = withOAuth(UsersApi);
const CQEShiftPresetsAPI = withOAuth(ShiftPresetsApi);

export const getCareOrganisationsAPI = () =>
  new CQECareOrganisationsAPI(
    new Configuration({ basePath: Constants.api.baseUrl }),
  );

export const getCareUnitsAPI = () =>
  new CQECareUnitsAPI(new Configuration({ basePath: Constants.api.baseUrl }));

export const getGreetingsAPI = () =>
  new CQEGreetingsAPI(new Configuration({ basePath: Constants.api.baseUrl }));

export const getRotasAPI = () =>
  new CQERotasAPI(new Configuration({ basePath: Constants.api.baseUrl }));

export const getShiftsAPI = () =>
  new CQEShiftsAPI(new Configuration({ basePath: Constants.api.baseUrl }));

export const getSurveysAPI = () =>
  new CQESurveysAPI(new Configuration({ basePath: Constants.api.baseUrl }));

export const getSurveyTemplatesAPI = () =>
  new CQESurveyTemplatesAPI(
    new Configuration({ basePath: Constants.api.baseUrl }),
  );

export const getUsersAPI = () =>
  new CQEUsersAPI(new Configuration({ basePath: Constants.api.baseUrl }));

export const getShiftPresetsAPI = () =>
  new CQEShiftPresetsAPI(
    new Configuration({ basePath: Constants.api.baseUrl }),
  );

export * from "./runtime";
export * from "./apis";
export * from "./models";
export * from "./NotLoggedInError";
